import React from "react";
import { useAppLoadingContext } from "../../../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../../../Context/ContextTypes/AppLoadingContextTypes";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import EarnRewardsSkeleton from "../Skeleton/EarnRewardsSkeleton";


//MUI Imports
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Link } from "@mui/material";

import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

type FieldItem = {
  itemId: string;
  itemImageUrl: string;
  itemImageWidth: string;
  itemImageHeight: string;
  itemImageAlt: string;
  itemRewardsHeader: string;
  itemRewardsSubHeader: string;
  itemRewardsDescription: string;
};

type EarnRewardsPropsTypes = {
  earnRewardsPageName: string;
  earnRewardsId: string;
  earnRewardsBgColor: string;
  earnRewardsBgDesktopHeight: string;
  earnRewardsBgTabletHeight: string;
  earnRewardsBgMobileHeight: string;
  earnRewardsHeaderText: string;
  earnRewardsHeaderTextColor: string;
  earnRewardsDescription: string;
  earnRewardsDescriptionTextColor: string;
  earnRewardsDetailsItems: FieldItem[];
  earnRewardsLinkText: string;
  earnRewardsLinkUrl: string;
  earnRewardsLinkAriaLabel: string;
  isInEditor: boolean;
  cqPath: string;
};

export default function EarnRewardsReact(props: EarnRewardsPropsTypes) {
  const { translationsData, translationsIsLoading } =
    useAppLoadingContext() as AppLoadingContextTypes;

  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    earnRewardsHeader: DomPurify(props.earnRewardsHeaderText),
    earnRewardsDescription: DomPurify(props.earnRewardsDescription),
  };

  const isCreditProtectorPage = props.earnRewardsPageName === "creditProtector";

  const numberOfItems = props.earnRewardsDetailsItems?.length;
  const itemWidthDesktop = Number(12 / numberOfItems);
  return (
    <Box
      component="section"
      id="earnRewardsSection"
      className="content-section"
      sx={{
        margin:"0px",
        //linear-gradient(0deg, white 245px, red 245px)
        backgroundImage: {
          xs: `linear-gradient(0deg, #ffffff ${props.earnRewardsBgMobileHeight}%, ${props.earnRewardsBgColor} ${props.earnRewardsBgMobileHeight}%)`,
          sm: `linear-gradient(0deg,#ffffff ${props.earnRewardsBgTabletHeight}%, ${props.earnRewardsBgColor} ${props.earnRewardsBgTabletHeight}%)`,
          md: `linear-gradient(0deg, #ffffff ${props.earnRewardsBgTabletHeight}%, ${props.earnRewardsBgColor} ${props.earnRewardsBgTabletHeight}%)`,
          lg: `linear-gradient(0deg, #f9f9f8 ${props.earnRewardsBgDesktopHeight}%, ${props.earnRewardsBgColor} ${props.earnRewardsBgDesktopHeight}%)`,
        },
      }}
    >
      {/* component h2 header text */}
      {translationsIsLoading ? (
        <EarnRewardsSkeleton />
      ) : (
        <>
          <>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                color: `${props.earnRewardsHeaderTextColor} !important`,
                mt: isCreditProtectorPage ? "80px !important" : "60px !important",
                mb: isCreditProtectorPage ? "16px !important" : "30px !important",
                fontSize: {lg:"36px !important",sm:"32px!important",xs:"28px"},
                padding:{xs:"0px 40px"}
              }}
            >
              {!translatedData.earnRewardsHeader
                ? "Header i18n tag missing."
                : translatedData.earnRewardsHeader}
            </Typography>
            {props.earnRewardsDescriptionTextColor && (
              <Typography
                variant="h3"
                gutterBottom
                textAlign="center"
                margin={{ xs: "0% 6%", sm: "0% 6%", md: "0% 6%", lg: "0% 15%" }}
                textTransform="initial"
                sx={{
                  color: `${props.earnRewardsDescriptionTextColor} !important`,
                  mt: isCreditProtectorPage ? "16px !important" : "25px",
                  mb: isCreditProtectorPage ? "60px !important" : "30px",
                  fontWeight: isCreditProtectorPage ? "normal !important" : "800 !important",
                  fontSize: "14px !important",
                }}
              >
                {!translatedData.earnRewardsDescription
                  ? "Header i18n tag missing."
                  : translatedData.earnRewardsDescription}
              </Typography>
            )}
          </>

          {/* rewards cards container */}
          <Container
            style={{ maxWidth: "1296px" }}
            sx={{
              padding: { xs: "0px 15px", sm: "0px 13px", md: "0px 13px", lg: "0px 16px" },
            }}
          >
            <Grid container spacing={3} alignItems="stretch">
              {/* reward card items */}
              {props.earnRewardsDetailsItems?.map((item: FieldItem, index: number) => (
                <Grid
                  item
                  xs={12}
                  sm={numberOfItems > 3 ? 6 : 12}
                  md={numberOfItems > 3 ? 6 : 12}
                  lg={itemWidthDesktop}
                  key={item.itemId}
                  style={{ display: "flex" }}
                  id={`cardContainer${item.itemId}`}
                >
                  <Card
                    sx={{
                      maxWidth: { md: "100% !important" },
                      width: { xs: "100% !important" },
                      boxShadow: "2px 6px 18px 2px rgba(38, 38, 38, 0.1)"
                    }}
                  >
                    <CardContent
                      sx={{
                        padding: isCreditProtectorPage ? "20px" : "0 !important",
                        textAlign: { xs: "left" },
                      }}
                    >
                      {/* reward card image. Checks if config is i18n value or not */}
                      {!DomPurify(item.itemImageUrl) ? (
                        <Typography gutterBottom component="div" variant="body1">
                          Image Url i18n missing
                        </Typography>
                      ) : (
                        <LazyLoadImage
                          effect="blur"
                          style={{ width: "auto" }}
                          src={DomPurify(item.itemImageUrl)}
                          alt=""
                          width={item.itemImageWidth + "px"}
                          height={item.itemImageHeight + "px"}
                          role="presentation"
                        />
                      )}
                      <CardContent
                        sx={{
                          padding: !isCreditProtectorPage ? "20px" : "0 !important",
                        }}
                      >
                        {/* reward card header. Checks if config is i18n value or not */}
                        <Typography
                          gutterBottom
                          variant="h3"
                          sx={{
                            fontSize:"24px!important",
                            marginBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px" },
                            textAlign: { xs: "left", lg: "left" },
                          }}
                        >
                          {item.itemRewardsHeader && (
                            <Grid
                              item
                              component="div"
                              sx={{
                                fontSize:"24px",
                                lineHeight:"normal",
                              }}
                            >
                              {parse(DomPurify(item.itemRewardsHeader))}
                            </Grid>
                          )}
                          {item.itemRewardsSubHeader && (
                            <Grid
                              item
                              component="div"
                              sx={{
                                fontSize: "18px",
                                lineHeight: "28px",
                              }}
                            >
                              {parse(DomPurify(item.itemRewardsSubHeader))}
                            </Grid>
                          )}
                        </Typography>
                        {/* reward card description. Checks if config is i18n value or not */}
                        {item.itemRewardsDescription && (
                          <Typography
                            variant="body2"
                            className="rewardsDescription"
                            sx={{
                              marginTop: { xs: "10px", sm: "10px", md: "10px", lg: "10px" },
                            }}
                          >
                            {parse(DomPurify(item.itemRewardsDescription))}
                          </Typography>
                        )}
                      </CardContent>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>


        </>
      )}
    </Box>
  );
}
